import React, { useContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import Home from "./pages/home/Home";
// `Ф0`: кабинет ПЗС — своё дерево `/cabinet/*`, без общего Layout.
import CabinetLayout from "./features/cabinet/CabinetLayout";
import CabinetStationsPage from "./features/cabinet/pages/CabinetStationsPage";
import CabinetConsumptionPage from "./features/cabinet/pages/CabinetConsumptionPage";
import CabinetProfilePage from "./features/cabinet/pages/CabinetProfilePage";
import {
  CABINET_ROLES,
  canEnterCabinet,
  isCabinetRole,
} from "./features/cabinet/cabinetRoles";
import Organization from "./components/organization/Organization";
// import Plant from './components/plant/Plant';
import Plant from "./pages/plants/PlantsPage";
import AllModules from "./pages/allModules/AllModules";
import AllStations from "./pages/allStations/AllStations";
import InPlants from "./features/inplants/pages/InPlants";
import Users from "./pages/users/Users";
import ModuleChartDashboard from "./pages/moduleChart/ModuleChartDashboard";
import RecoveryPage from "./pages/recovery/RecoveryPage";
// п. 20 (ФЗ-152, РГ-08): активация приглашения — открыта до входа.
import ActivationPage from "./pages/activation/ActivationPage";


import LogsViewer from "./components/devel/logs/LogsViewer";
import FirmwareUpdatePanel from "./components/devel/firmware/FirmwareUpdatePanel";
import AlarmRecipientsPage from "./components/admin/AlarmRecipientsPage";
import DBLogsViewer from "./components/devel/DBlogs/DBlogsviever";
import ProtectedRoute from "./ProtectedRoute";
import DevLogsViewer from "./components/devel/Devlogs/DevLogsViewer";
// п. 23 (`DEV-68`, ФЗ-152): экран внесения по договору — только root.
import InvitationsPage from "./pages/invitations/InvitationsPage";

import { Context } from "./index";
import { observer } from "mobx-react-lite";
// DEV-89 (`oq-ММБ-121` (а)): возврат на исходную страницу после входа.
import { landingForRole, peekReturnPath } from "./utils/returnPath";
// `32.1`: прежний ключ пароля убираем **при загрузке приложения**, до `checkAuth`.
import { forgetLegacyPasswordKey } from "./utils/legacyPasswordKey";

// Чистка идёт при загрузке модуля, то есть раньше эффектов и раньше первого
// обращения за сессией (`checkAuth` ниже): человек, уже вошедший, форму входа не
// открывает, и ключ с паролем иначе дожил бы до работы с системой.
forgetLegacyPasswordKey();

// Функция для определения пути после входа
const getRedirectPath = (role) => {
  // Страницы ролей живут в `utils/returnPath.ts`: там же рядом правило возврата,
  // и два перечня про одно и то же разошлись бы.
  return landingForRole(role);
};

// Компонент для редиректа после авторизации
const AuthRedirectHandler = observer(() => {
  const navigate = useNavigate();
  const { store } = useContext(Context);

  useEffect(() => {
    // Если пользователь авторизован и есть роль
    if (!store.isAuth || !store.user?.role) return;

    const role = store.user.role;
    const currentPath = `${window.location.pathname}${window.location.search}`;
    // DEV-89: если человека застало истечение сессии на рабочей странице, после
    // входа он возвращается туда — вместе с параметрами. Страница по роли —
    // только когда возвращаться некуда (прямой заход или сама страница входа).
    const returnPath = peekReturnPath(currentPath);

    // `Ф0` (`АРХ-ЛК-20.10`): ПЗС и владелец станции попадают в кабинет.
    // Внутри `/cabinet` редирект не мешает: человек ходит по своим разделам.
    // `/profile` ведёт в `/cabinet/profile` — прежний адрес остаётся рабочим.
    if (isCabinetRole(role)) {
      if (returnPath) {
        if (currentPath !== returnPath) navigate(returnPath, { replace: true });
        return;
      }

      if (currentPath.startsWith("/cabinet")) return;

      const target = currentPath.startsWith("/profile")
        ? "/cabinet/profile"
        : getRedirectPath(role);

      if (currentPath !== target) navigate(target, { replace: true });
      return;
    }

    // `root` допущен в кабинет: если он уже там, в админку не уводим.
    if (currentPath.startsWith("/cabinet") && canEnterCabinet(role)) return;

    // Остальные роли — один раз; возврат — всегда.
    const alreadyRedirected = localStorage.getItem("authRedirectDone");

    if (!returnPath && alreadyRedirected) return;

    const redirectPath = returnPath || getRedirectPath(role);

    if (currentPath !== redirectPath) {
      navigate(redirectPath, { replace: true });

      if (!returnPath) {
        localStorage.setItem("authRedirectDone", "true");
      }
    }
  }, [store.isAuth, store.user?.role]);

  return null;
});

export default observer(function App() {
  const { store } = useContext(Context);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function verifyAuth() {
      // БЕЗ-01 (DEV-55): сессия — в httpOnly-cookie, из хранилища страницы её
      // не видно. Продление само скажет, есть ли сессия; отказ — норма для
      // анонимного посетителя.
      await store.checkAuth();
      setIsLoading(false);
    }
    verifyAuth();
  }, []);

  if (isLoading) return <div>Загрузка...</div>;

  return (
    <Router>
      {/* 1. Редирект-хендлер — внутри Router, но вне Routes */}
      <AuthRedirectHandler />

      {/* 2. Все роуты */}
      <Routes>
        {/* 3. Все страницы, требующие основной Layout */}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />

          <Route
            path="/organizations"
            element={
              <ProtectedRoute
                element={<Organization />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/plants"
            element={
              <ProtectedRoute
                element={<Plant />}
                allowedRoles={["root", "оператор организации"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/inplants"
            element={
              <ProtectedRoute
                element={<InPlants />}
                allowedRoles={[
                  "root",
                  "оператор организации",
                  "оператор объекта",
                ]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/modules"
            element={
              <ProtectedRoute
                element={<AllModules />}
                allowedRoles={["root", "оператор организации"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/Mymodule-charts/:id/:charge_id/:line_name"
            element={
              <ProtectedRoute
                element={<ModuleChartDashboard />}
                allowedRoles={[
                  "root",
                  "оператор организации",
                  "оператор объекта",
                ]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/module-charts/:type/:id/:charge_id/:line_name"
            element={
              <ProtectedRoute
                element={<ModuleChartDashboard />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/stations"
            element={
              <ProtectedRoute
                element={<AllStations />}
                allowedRoles={["root", "оператор организации"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute
                element={<Users />}
                allowedRoles={["root", "оператор организации"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          <Route
            path="/logs"
            element={
              <ProtectedRoute
                element={<LogsViewer variant="legacy" />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
        </Route>
        {/* `Ф0` (`АРХ-ЛК-20.10`): кабинет ПЗС — своё дерево, без общего Layout.
            Меню — Станции, Потребление, Профиль. Оператора объекта сюда не
            пускают: его роль не входит в `CABINET_ROLES`. */}
        <Route
          path="/cabinet"
          element={
            <ProtectedRoute
              element={<CabinetLayout />}
              allowedRoles={CABINET_ROLES}
              isAuthenticated={store.isAuth}
              userRole={store.user?.role}
            />
          }
        >
          <Route index element={<Navigate to="stations" replace />} />
          <Route path="stations" element={<CabinetStationsPage />} />
          <Route path="consumption" element={<CabinetConsumptionPage />} />
          <Route path="profile" element={<CabinetProfilePage />} />
        </Route>

        {/* Прежний адрес профиля остаётся рабочим: `/profile` → кабинет. */}
        <Route path="/profile" element={<Navigate to="/cabinet/profile" replace />} />

        {/* Админка */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="logs" />} />

          <Route
            path="logs"
            element={
              <ProtectedRoute
                element={<LogsViewer variant="legacy" />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
          <Route
            path="logs-new-protocol"
            element={
              <ProtectedRoute
                element={<LogsViewer variant="new-protocol" />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
          <Route
            path="DBlogs"
            element={
              <ProtectedRoute
                element={<DBLogsViewer />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
          <Route
            path="updatePanel"
            element={
              <ProtectedRoute
                element={<FirmwareUpdatePanel />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
          <Route
            path="alarm-recipients"
            element={
              <ProtectedRoute
                element={<AlarmRecipientsPage />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
          <Route
            path="devlog"
            element={
              <ProtectedRoute
                element={<DevLogsViewer />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />

          {/* `R.4`: неизвестный адрес админки — на «Логи», не пустая страница. */}
          <Route path="*" element={<Navigate to="logs" replace />} />

          {/* п. 23 (`DEV-68`, ФЗ-152): внесение по договору — только root. */}
          <Route
            path="invitations"
            element={
              <ProtectedRoute
                element={<InvitationsPage />}
                allowedRoles={["root"]}
                isAuthenticated={store.isAuth}
                userRole={store.user?.role}
              />
            }
          />
        </Route>

        {/* 4. Страница без Layout (можно оставить так) */}
        <Route
          path="/unauthorized"
          element={<h2>У вас нет доступа к этой странице</h2>}
        />

        {/* DEV-61: восстановление доступа — открыто без входа и вне Layout */}
        <Route path="/recovery" element={<RecoveryPage />} />

        {/* п. 20 (ФЗ-152): активация приглашения — тоже до входа и вне Layout */}
        <Route path="/activation" element={<ActivationPage />} />
      </Routes>
    </Router>
  );
});
